import React, { useRef, useState, useEffect } from 'react';
import { UploadCloud, Loader2, X, Plus, Palette as PaletteIcon, Edit2, Tag, Camera, Image as ImageIcon, ArrowLeft } from 'lucide-react';
import { createProduct, updateProduct, extractColorVariants } from '../api/product.api';
import { uploadImageToS3, compressImage } from '../utils/s3Upload';
import TextureSelector from './TextureSelector';
import { useBackButton } from '../hooks/useBackButton';

const ProductAddForm = ({ onClose, onProductAdded, onProductUpdated, product }) => {
  useBackButton(true, () => { if (onClose) onClose(); });
  const isEditMode = !!product;
  
  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [shadeCards, setShadeCards] = useState([]); // Changed to array to support multiple shade cards
  const [isExtractingColors, setIsExtractingColors] = useState(false);
  const [extractedPalette, setExtractedPalette] = useState([]);
  const [showExtractionSuccess, setShowExtractionSuccess] = useState(false);
  const [productName, setProductName] = useState(product?.name || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingColorIndex, setEditingColorIndex] = useState(null);
  const fileInputRef = useRef();
  const shadeCardInputRef = useRef();

  // S3 Upload configuration
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Color variants state - simplified for fabric uploads
  const [colorVariants, setColorVariants] = useState([]);
  const variantFileInputsRef = useRef([]);
  
  // Fabric texture state
  const [selectedTexture, setSelectedTexture] = useState(null);

  // Tags state
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  
  // Camera state
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraMode, setCameraMode] = useState('main'); // 'main' or 'shadeCard'
  const [stream, setStream] = useState(null);
  const [cameraFacingMode, setCameraFacingMode] = useState('environment'); // 'user' for front, 'environment' for back
  const videoRef = useRef(null);
  const canvasRef = useRef(null);



  // Initialize form with product data if editing
  useEffect(() => {
    if (product) {
      setProductName(product.name || '');
      
      // Set image if product has one
      if (product.image || product.images?.original?.imgbbUrl || product.images?.original?.url || product.images?.original?.s3Url) {
        const imageUrl = product.images?.original?.s3Url || product.images?.original?.imgbbUrl || product.images?.original?.url || product.image;
        setImage(imageUrl);
      }

      // Set color variants if exist
      if (product.colorVariants && product.colorVariants.length > 0) {
        setColorVariants(product.colorVariants.map((variant, idx) => ({
          colorName: variant.colorName || `Color ${idx + 1}`,
          hex: variant.hex || '#000000',
          selected: true
        })));
      }

      // Set fabric texture if exists
      if (product.fabricTexture) {
        setSelectedTexture(product.fabricTexture);
      }

      // Set tags if exist
      if (product.tags && Array.isArray(product.tags) && product.tags.length > 0) {
        setTags(product.tags);
      }

      // Shade cards - support both legacy single and new multiple shade cards
      if (product.shadeCards && Array.isArray(product.shadeCards) && product.shadeCards.length > 0) {
        console.log('📋 Loading shade cards:', product.shadeCards);
        setShadeCards(product.shadeCards.map((sc, index) => ({
          url: sc.url,
          preview: sc.url,
          file: null,
          extractedColors: [] // Colors already in colorVariants
        })));
        console.log('✅ Loaded', product.shadeCards.length, 'shade cards');
      } else if (product.shadeCard?.url) {
        // Legacy support - convert single shade card to array
        console.log('📋 Loading legacy single shade card:', product.shadeCard);
        setShadeCards([{
          url: product.shadeCard.url,
          preview: product.shadeCard.url,
          file: null,
          extractedColors: []
        }]);
        console.log('✅ Loaded 1 legacy shade card');
      } else {
        console.log('ℹ️ No shade cards found in product');
      }
    }
  }, [product]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  useEffect(() => {
    return () => {
      if (typeof image === 'string' && image.startsWith('blob:')) {
        URL.revokeObjectURL(image);
      }
    };
  }, [image]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImage(URL.createObjectURL(file));
      setError(''); // Clear any previous errors
    }
  };

  const handleShadeCardFile = async (file) => {
    if (!file) return;
    
    setError('');
    
    // Upload shade card to S3, then extract colors
    try {
      setIsExtractingColors(true);
      const compressed = await compressImage(file, { maxWidth: 1600, maxHeight: 1600, quality: 0.8 });
      const uploadRes = await uploadImageToS3(compressed, {
        folder: 'shadecards',
        fileName: `shadecard_${Date.now()}_${compressed.name}`
      });
      
      const apiRes = await extractColorVariants(uploadRes.url, 100); // Extract up to 100 colors
      const palette = apiRes.palette || apiRes.data?.palette || [];
      
      // Add the new shade card to the array
      setShadeCards(prev => [...prev, {
        url: uploadRes.url,
        preview: uploadRes.url,
        file: file,
        extractedColors: palette
      }]);
      
      // Merge new colors with existing ones (avoid duplicates by hex value)
      setColorVariants(prevVariants => {
        const existingHexes = new Set(prevVariants.map(v => v.hex.toLowerCase()));
        const newColors = Array.isArray(palette) ? palette
          .filter(c => !existingHexes.has((c.hex || '').toLowerCase()))
          .map((c, idx) => ({
            colorName: c.name || `Color ${prevVariants.length + idx + 1}`,
            hex: c.hex || '#000000',
            selected: true
          })) : [];
        return [...prevVariants, ...newColors];
      });
      
      // Show success message with count
      if (palette.length > 0) {
        console.log(`✅ Successfully extracted ${palette.length} colors from shade card`);
        setShowExtractionSuccess(true);
        setTimeout(() => setShowExtractionSuccess(false), 3000);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to extract color palette');
    } finally {
      setIsExtractingColors(false);
      // Reset file input so same file can be selected again
      if (shadeCardInputRef.current) {
        shadeCardInputRef.current.value = '';
      }
    }
  };

  const handleShadeCardChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    await handleShadeCardFile(file);
  };

  const handleRemoveShadeCard = (index) => {
    setShadeCards(prev => prev.filter((_, i) => i !== index));
    setError('');
  };
  
  const handleColorEdit = (index, newHex) => {
    setColorVariants(prev => prev.map((variant, i) => 
      i === index ? { ...variant, hex: newHex } : variant
    ));
  };

  // Handle tags
  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim() !== '') {
    e.preventDefault();
      const newTag = tagInput.trim();
      if (!tags.includes(newTag)) {
        setTags([...tags, newTag]);
        setTagInput('');
      }
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  // Camera functions
  const openCamera = (mode = 'main') => {
    console.log('Opening camera in mode:', mode);
    setCameraMode(mode);
    setIsCameraOpen(true);
  };

  const startCamera = async () => {
    try {
      // Stop existing stream if any
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: cameraFacingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      setStream(mediaStream);
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Unable to access camera. Please check permissions or choose from device instead.');
      setIsCameraOpen(false);
    }
  };

  const closeCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsCameraOpen(false);
  };

  const switchCamera = async () => {
    try {
      // Stop existing stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      
      // Toggle camera facing mode
      const newFacingMode = cameraFacingMode === 'user' ? 'environment' : 'user';
      setCameraFacingMode(newFacingMode);
      
      // Get new stream with different camera
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: newFacingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      setStream(mediaStream);
    } catch (err) {
      console.error('Error switching camera:', err);
      setError('Unable to switch camera. Please try again.');
    }
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) {
      console.error('Video or canvas ref not available');
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Check if video is ready and playing
    if (video.readyState !== video.HAVE_ENOUGH_DATA || video.videoWidth === 0 || video.videoHeight === 0) {
      console.error('Video not ready', { readyState: video.readyState, width: video.videoWidth, height: video.videoHeight });
      setError('Camera is not ready. Please wait a moment and try again.');
      return;
    }

    const context = canvas.getContext('2d');

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to blob
    canvas.toBlob(async (blob) => {
      if (blob) {
        try {
          // Create a File object from the blob
          const file = new File([blob], `camera-capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
          
          if (cameraMode === 'main') {
            // Handle main image capture - create object URL first
            const imageUrl = URL.createObjectURL(blob);
            setImage((prev) => {
              if (typeof prev === 'string' && prev.startsWith('blob:')) {
                URL.revokeObjectURL(prev);
              }
              return imageUrl;
            });
            
            // Close camera first to ensure modal doesn't block the preview
            closeCamera();
            
            // Use setTimeout to ensure camera closes before updating state
            setTimeout(() => {
              // Set both file and image URL
              console.log('Setting image state with URL:', imageUrl);
              setImageFile(file);
              setImage(imageUrl);
              setError('');
              console.log('Image state updated');
            }, 200);
          } else if (cameraMode === 'shadeCard') {
            // Handle shade card capture
            closeCamera();
            setTimeout(async () => {
              await handleShadeCardFile(file);
            }, 200);
          }
        } catch (error) {
          console.error('Error processing captured photo:', error);
          setError('Failed to process captured photo. Please try again.');
          closeCamera();
        }
      } else {
        console.error('Failed to create blob from canvas');
        setError('Failed to capture photo. Please try again.');
        closeCamera();
      }
    }, 'image/jpeg', 0.9);
  };

  // Clean up stream on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  // Initialize camera when modal opens
  useEffect(() => {
    if (isCameraOpen && !stream) {
      startCamera();
    }
    // Cleanup: close camera if component unmounts while camera is open
    return () => {
      if (isCameraOpen && stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [isCameraOpen]);
  
  // Update video source when stream changes
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      // Ensure video plays
      videoRef.current.play().catch(err => {
        console.error('Error playing video:', err);
      });
    }
  }, [stream]);

  // Removed URL-based extraction; handled automatically on shade card upload


  const validateForm = () => {
    if (!isEditMode && !imageFile && !image) {
      setError('Please upload a fabric image');
      return false;
    }
    if (!productName.trim()) {
      setError('Please enter a fabric name');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const startTime = Date.now();
    console.log('🚀 Starting product creation process (frontend)...');
    
    setIsLoading(true);
    setError('');

    try {
      let imageUrl = image;
      
      // Upload new image to S3 if a new file was selected
      if (imageFile) {
        const imageUploadStartTime = Date.now();
        console.log('🖼️ Starting image upload process...');
        
        try {
          // Compress image before upload for better performance
          const compressionStartTime = Date.now();
          console.log('🗜️ Compressing image...');
          
          const compressedFile = await compressImage(imageFile, {
            maxWidth: 1200,
            maxHeight: 800,
            quality: 0.8
          });
          
          const compressionTime = Date.now() - compressionStartTime;
          console.log(`⏱️ Image compression completed in ${compressionTime}ms`);

          // Upload to S3
          const s3UploadStartTime = Date.now();
          console.log('☁️ Uploading image to S3...');
          
          const uploadResult = await uploadImageToS3(compressedFile, {
            folder: 'products',
            fileName: `product_${Date.now()}_${compressedFile.name}`,
            onProgress: (progress) => {
              setUploadProgress(progress);
            }
          });
          
          const s3UploadTime = Date.now() - s3UploadStartTime;
          console.log(`⏱️ S3 upload completed in ${s3UploadTime}ms`);

          imageUrl = uploadResult.url;

          const totalImageUploadTime = Date.now() - imageUploadStartTime;
          console.log(`⏱️ Total image upload time: ${totalImageUploadTime}ms`);
        } catch (uploadError) {
          const imageUploadTime = Date.now() - imageUploadStartTime;
          console.error(`❌ S3 upload error (after ${imageUploadTime}ms):`, uploadError);
          throw new Error(`Image upload failed: ${uploadError.message}`);
        }
      }

      // Build variants from all palette entries (no selection filtering)
      const uploadedVariants = (colorVariants || [])
        .map((v, i) => ({
          colorName: v.colorName || `Variant ${i + 1}`,
          hex: v.hex || '#000000',
          image: { url: '', s3Url: '', s3Key: null }
        }));

      // Prepare product data with default values for simplified form
      const productData = {
        name: productName.trim(),
        category: 'fabric', // Default to fabric category for simplified form
        image: imageUrl,
        // Store image data with both legacy and S3 support
        images: {
          original: {
            url: imageUrl,
            s3Url: imageFile ? imageUrl : null, // Only set S3 URL for new uploads
            imgbbUrl: !imageFile ? (product?.images?.original?.imgbbUrl || null) : null // Keep existing ImgBB URL if not uploading new image
          }
        },
        description: productName.trim(), // Use product name as description for simplified form
        price: product?.price || 0,
        targetAudience: 'unisex', // Default to unisex for simplified form
        isUnstitched: true, // Default to unstitched (fabric) for simplified form
        removeBackground: product?.removeBackground !== false, // Keep existing setting or default to true
        productType: 'fabric' // Default product type for fabric
      };


      // Include color variants if provided
      if (uploadedVariants.length > 0) {
        productData.colorVariants = uploadedVariants;
      } else {
        // If no color variants selected, clear them from the database
        productData.colorVariants = [];
      }

      // Handle multiple shade cards - include if exists, explicitly remove if not
      if (shadeCards && shadeCards.length > 0) {
        productData.shadeCards = shadeCards.map(sc => ({ url: sc.url }));
        // For backward compatibility, also set the first shade card as legacy shadeCard
        productData.shadeCard = { url: shadeCards[0].url };
      } else {
        // Explicitly remove shade cards from database if removed
        productData.shadeCards = [];
        productData.shadeCard = null;
      }

      // Include fabric texture if selected
      if (selectedTexture) {
        productData.fabricTexture = {
          name: selectedTexture.name,
          description: selectedTexture.description,
          categoryName: selectedTexture.categoryName
        };
      }

      // Include tags if provided
      if (tags && tags.length > 0) {
        productData.tags = tags;
      } else {
        productData.tags = [];
      }

      const apiCallStartTime = Date.now();
      console.log('📡 Making API call to create/update product...');
      
      let result;
      if (isEditMode) {
        // Update existing product
        result = await updateProduct(product._id, productData);
        if (onProductUpdated) {
          onProductUpdated(result);
        }
      } else {
        // Create new product
        result = await createProduct(productData);
        if (onProductAdded) {
          onProductAdded(result);
        }
      }
      
      const apiCallTime = Date.now() - apiCallStartTime;
      console.log(`⏱️ API call completed in ${apiCallTime}ms`);

      // Reset form only for new products
      if (!isEditMode) {
        setImage(null);
        setImageFile(null);
        setProductName('');
        setUploadProgress(0);
        setColorVariants([]);
        setSelectedTexture(null);
        setTags([]);
        setTagInput('');
      }

      // Close modal if provided
      if (onClose) {
        onClose();
      }

      const totalTime = Date.now() - startTime;
      console.log(`🎉 Total product creation process completed in ${totalTime}ms`);
      console.log(`📦 Product ${isEditMode ? 'updated' : 'created'} successfully: ${result.name}`);

    } catch (error) {
      const totalTime = Date.now() - startTime;
      console.error(`❌ Error ${isEditMode ? 'updating' : 'creating'} product (after ${totalTime}ms):`, error);
      
      // Handle specific S3 errors
      if (error.message.includes('S3') || error.message.includes('AWS') || error.message.includes('upload failed')) {
        setError('Image upload failed. Please check your S3 configuration and try again.');
      } else {
        setError(error.response?.data?.message || error.message || `Failed to ${isEditMode ? 'update' : 'create'} product`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm overflow-hidden">
      <div className="h-full w-full flex flex-col bg-white">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 sm:px-6 pb-4 pt-[max(1rem,env(safe-area-inset-top))] flex items-center justify-between shadow-lg">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
      {onClose && (
        <button
          onClick={onClose}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-white/20 transition-colors active:scale-95 touch-manipulation"
                title="Go back"
          disabled={isLoading}
        >
                <ArrowLeft size={24} />
        </button>
      )}
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">
          {isEditMode ? 'Edit Fabric' : 'Add Fabric'}
        </h1>
              <p className="text-sm text-purple-100">
                {isEditMode ? 'Update fabric details' : 'Upload your fabric images for kit generation'}
        </p>
      </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors touch-manipulation"
              title="Close"
              disabled={isLoading}
            >
              <X size={24} />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto p-4 sm:p-6">
      {/* Error message */}
      {error && (
              <div className="mb-3 sm:mb-4 p-2.5 sm:p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs sm:text-sm">
          {error}
        </div>
      )}
      
      {/* Upload Progress */}
      {isLoading && uploadProgress > 0 && uploadProgress < 100 && (
              <div className="mb-3 sm:mb-4 p-2.5 sm:p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
                  <span className="text-blue-700 text-xs sm:text-sm font-medium">Uploading image...</span>
                  <span className="text-blue-600 text-xs sm:text-sm">{uploadProgress.toFixed(1)}%</span>
          </div>
                <div className="w-full bg-blue-200 rounded-full h-1.5 sm:h-2">
            <div 
                    className="bg-blue-600 h-1.5 sm:h-2 rounded-full transition-all duration-300" 
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        </div>
      )}
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {/* Fabric name */}
        <div>
                <label className="block text-gray-700 font-medium mb-1.5 sm:mb-2 text-sm sm:text-base">Fabric Name</label>
          <input
            type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., Cotton Shirt Fabric"
            value={productName}
            onChange={e => setProductName(e.target.value)}
            disabled={isLoading}
            required
          />
        </div>

        {/* Fabric Texture Selection */}
        <div>
                <label className="block text-gray-700 font-medium mb-1.5 sm:mb-2 text-sm sm:text-base">Fabric Texture (optional)</label>
          <TextureSelector
            selectedTexture={selectedTexture}
            onTextureChange={setSelectedTexture}
            showTip={false}
            onTipToggle={() => {}}
          />
        </div>

              {/* Tags Input */}
              <div>
                <label className="block text-gray-700 font-medium mb-1.5 sm:mb-2 text-sm sm:text-base">Tags (optional)</label>
                <div className="space-y-2">
                  {/* Tag Input Field */}
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 pr-9 sm:pr-10 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Type a tag and press Enter"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleAddTag}
                      disabled={isLoading}
                    />
                    <Tag size={16} className="absolute right-2.5 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 sm:w-[18px] sm:h-[18px]" />
                  </div>
                  
                  {/* Tags Display */}
                  {tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 sm:gap-2 p-2 sm:p-3 bg-gray-50 rounded-lg border border-gray-200">
                      {tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 bg-blue-100 text-blue-800 rounded-full text-xs sm:text-sm font-medium"
                        >
                          <span>{tag}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-200 rounded-full p-0.5 transition-colors touch-manipulation"
                            disabled={isLoading}
                            aria-label={`Remove tag ${tag}`}
                          >
                            <X size={12} className="sm:w-3.5 sm:h-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 leading-relaxed">Add tags to help organize and search your fabrics (e.g., "cotton", "summer", "formal")</p>
                </div>
        </div>

        {/* Upload Fabric Image */}
        <div>
          <label className="block text-gray-700 font-medium mb-2">Main Fabric Image</label>
                
                {!image ? (
                  <div className="space-y-3">
                    {/* Action Buttons */}
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => openCamera('main')}
                        disabled={isLoading}
                        className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Camera size={20} />
                        <span>Take Photo</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current.click()}
                        disabled={isLoading}
                        className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ImageIcon size={20} />
                        <span>Choose from Device</span>
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 text-center">Recommended: High resolution (1024x1024+ pixels)</p>
                  </div>
                ) : (
              <div className="relative inline-block">
                    <img 
                      src={image} 
                      alt="Fabric preview" 
                      className="h-32 w-32 object-cover rounded-lg shadow-md"
                      onError={(e) => {
                        console.error('Image failed to load:', image);
                        setError('Failed to load image preview');
                      }}
                    />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                        // Revoke object URL to prevent memory leaks
                        if (image && image.startsWith('blob:')) {
                          URL.revokeObjectURL(image);
                        }
                    setImage(null);
                    setImageFile(null);
                  }}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold hover:bg-red-600 transition-colors"
                  aria-label="Remove image"
                  disabled={isLoading}
                >
                  ×
                </button>
              </div>
                )}
                
          <input
            type="file"
            accept="image/*"
            className="hidden"
            ref={fileInputRef}
            onChange={handleImageChange}
            disabled={isLoading}
          />
                <canvas ref={canvasRef} className="hidden" />
        </div>

        {/* Shade Cards (optional) - Support Multiple */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"></div>
              <label className="text-gray-800 font-semibold text-lg">Shade Cards</label>
              <span className="text-sm text-gray-500 font-normal">(optional)</span>
            </div>
            {shadeCards.length > 0 && (
              <span className="text-xs text-gray-600 bg-purple-100 px-2 py-1 rounded-full">
                {shadeCards.length} card{shadeCards.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
          
          {/* Existing Shade Cards Grid */}
          {shadeCards.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              {shadeCards.map((shadeCard, index) => (
                <div key={index} className="relative bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden group">
                  {/* Shade Card Preview */}
                  <div className="relative">
                    <img 
                      src={shadeCard.preview || shadeCard.url} 
                      alt={`Shade card ${index + 1}`} 
                      className="w-full h-40 object-cover" 
                    />
                    
                    {/* Card Number Badge */}
                    <div className="absolute top-2 left-2 bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                      #{index + 1}
                    </div>
                    
                    {/* Remove button */}
                    <button
                      type="button"
                      onClick={() => handleRemoveShadeCard(index)}
                      className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                      disabled={isLoading}
                    >
                      <X size={16} />
                    </button>
                  </div>
                  
                  {/* Shade Card Info */}
                  <div className="p-3 bg-gray-50">
                    <p className="text-sm text-gray-600">
                      {shadeCard.extractedColors?.length > 0 
                        ? `${shadeCard.extractedColors.length} colors extracted` 
                        : 'Shade card uploaded'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
          
                {/* Add Shade Card Options */}
                <div className="space-y-3">
                    {isExtractingColors ? (
                    <div className="bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 border-2 border-purple-200 rounded-2xl p-6 text-center">
                      <div className="relative mb-3">
                        <div className="w-12 h-12 mx-auto bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                      <Loader2 size={20} className="text-white animate-spin" />
                  </div>
                </div>
                      <h3 className="text-base font-semibold text-gray-800">Extracting Colors...</h3>
                      <p className="text-sm text-gray-600 mt-1">Please wait...</p>
                    </div>
                  ) : (
                    <>
                      {/* Action Buttons */}
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => openCamera('shadeCard')}
                          disabled={isLoading || isExtractingColors}
                          className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Camera size={20} />
                          <span>Take Photo</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => shadeCardInputRef.current.click()}
                          disabled={isLoading || isExtractingColors}
                          className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ImageIcon size={20} />
                          <span>Choose from Device</span>
                        </button>
                </div>
                
                      <p className="text-xs text-gray-500 text-center">
                        We'll automatically extract color variants from your shade card
                      </p>
                
                {/* Success Message */}
                {showExtractionSuccess && (
                        <div className="flex items-center justify-center gap-2 text-green-600 animate-pulse p-3 bg-green-50 rounded-lg">
                    <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-white text-xs">✓</div>
                    <span className="text-sm font-medium">Colors extracted successfully!</span>
                  </div>
                )}
                    </>
                  )}
              
              <input
                ref={shadeCardInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleShadeCardChange}
                disabled={isLoading || isExtractingColors}
              />
          </div>

          {colorVariants && colorVariants.length > 0 && (
            <div className="mt-6">
              {/* Color Palette Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                    <PaletteIcon size={16} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Extracted Color Palette</h3>
                    <p className="text-sm text-gray-600">
                      {colorVariants.length} colors detected • Click to edit
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Color Grid with Edit Functionality */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 gap-y-6">
                {colorVariants.map((v, idx) => (
                  <div
                    key={idx}
                    className="group relative rounded-xl border-2 border-gray-200 bg-white hover:border-purple-300 hover:shadow-md transition-all duration-300"
                  >
                    {/* Color Swatch */}
                    <div className="relative h-20 w-full overflow-hidden rounded-t-xl">
                      <div 
                        className="w-full h-full" 
                        style={{ backgroundColor: v.hex || '#000' }}
                      />
                      {/* Edit Button Overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingColorIndex(editingColorIndex === idx ? null : idx);
                          }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity bg-white text-purple-600 rounded-full p-2 shadow-lg hover:bg-purple-50"
                          title="Edit color"
                        >
                          <Edit2 size={16} />
                        </button>
                      </div>
                    </div>
                    
                    {/* Color Info */}
                    <div className="p-3">
                      <div className="text-sm font-semibold text-gray-800 font-mono text-center">
                        {v.hex}
                      </div>
                    </div>
                    
                    {/* Color Picker Popup - Responsive for mobile and desktop */}
                    {editingColorIndex === idx && (
                      <>
                        {/* Backdrop to close popup when clicking outside */}
                        <div 
                          className="fixed inset-0 z-40 bg-black/20" 
                          onClick={() => setEditingColorIndex(null)}
                        />
                        
                        {/* Mobile: Fixed centered modal, Desktop: Positioned popup */}
                        <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 sm:absolute sm:top-full sm:left-0 sm:translate-x-0 sm:translate-y-0 sm:mt-2 z-50 bg-white rounded-xl shadow-2xl border-2 border-purple-300 p-4 w-[90vw] max-w-[280px] sm:w-auto sm:min-w-[250px]">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold text-gray-800 text-sm">Edit Color</h4>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingColorIndex(null);
                                }}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <X size={16} />
                              </button>
                            </div>
                            
                            {/* Color Picker */}
                            <div className="space-y-2">
                              <label className="block text-xs text-gray-600 font-medium">Pick a color</label>
                              <input
                                type="color"
                                value={v.hex}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleColorEdit(idx, e.target.value);
                                }}
                                className="w-full h-12 rounded-lg border-2 border-gray-300 cursor-pointer"
                              />
                            </div>
                            
                            {/* Hex Input */}
                            <div className="space-y-2">
                              <label className="block text-xs text-gray-600 font-medium">Hex Code</label>
                              <input
                                type="text"
                                value={v.hex}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const hex = e.target.value;
                                  if (/^#[0-9A-Fa-f]{0,6}$/.test(hex)) {
                                    handleColorEdit(idx, hex);
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="#000000"
                              />
                            </div>
                            
                            {/* Color Preview */}
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-8 h-8 rounded-lg border-2 border-gray-300"
                                style={{ backgroundColor: v.hex }}
                              />
                              <span className="text-xs text-gray-600">Preview</span>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
              
            </div>
          )}
        </div>

        {/* Save Fabric Button */}
        <button
          type="submit"
          className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-semibold text-lg shadow hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          disabled={isLoading || isExtractingColors}
        >
          {isExtractingColors ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              Extracting Colors from Shade Card...
            </>
          ) : isLoading ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              {isEditMode ? 'Updating Fabric...' : 'Saving Fabric...'}
            </>
          ) : (
            isEditMode ? 'Update Fabric' : 'Save Fabric'
          )}
        </button>
      </form>
          </div>
        </div>

        {/* Camera Modal */}
        {isCameraOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4">
            <div className="relative w-full max-w-4xl bg-black rounded-lg overflow-hidden">
              {/* Close Button */}
              <button
                type="button"
                onClick={closeCamera}
                className="absolute top-4 right-4 z-10 bg-red-500 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-red-600 transition-colors"
                aria-label="Close camera"
              >
                <X size={24} />
              </button>

                  {/* Video Preview */}
                  <div className="relative">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-auto max-h-[80vh] object-contain"
                      onLoadedMetadata={() => {
                        console.log('Video metadata loaded', {
                          videoWidth: videoRef.current?.videoWidth,
                          videoHeight: videoRef.current?.videoHeight,
                          readyState: videoRef.current?.readyState
                        });
                        // Ensure video plays
                        if (videoRef.current) {
                          videoRef.current.play().catch(err => {
                            console.error('Error playing video after metadata loaded:', err);
                          });
                        }
                      }}
                      onCanPlay={() => {
                        console.log('Video can play');
                        if (videoRef.current) {
                          videoRef.current.play().catch(err => {
                            console.error('Error playing video on canPlay:', err);
                          });
                        }
                      }}
                    />
                
                {/* Camera Controls */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <div className="flex items-center justify-center gap-4">
                    {/* Switch Camera Button */}
                    <button
                      type="button"
                      onClick={switchCamera}
                      className="bg-white/20 backdrop-blur-sm text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-white/30 transition-colors"
                      aria-label="Switch camera"
                    >
                      <Camera size={20} />
                    </button>
                    
                    {/* Capture Button */}
                    <button
                      type="button"
                      onClick={capturePhoto}
                      className="bg-white rounded-full w-16 h-16 flex items-center justify-center hover:bg-gray-200 transition-colors shadow-lg border-4 border-gray-300"
                      aria-label="Capture photo"
                    >
                      <div className="w-12 h-12 bg-white rounded-full border-2 border-gray-400"></div>
                    </button>
                    
                    {/* Cancel Button */}
                    <button
                      type="button"
                      onClick={closeCamera}
                      className="bg-white/20 backdrop-blur-sm text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-white/30 transition-colors"
                      aria-label="Cancel"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductAddForm; 